'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  fieldControlClassName,
  fieldDensityClassName,
  type FieldControlSize,
  type FieldDensity,
} from './input';
import {
  ISO_DATE_RE,
  addDaysIso,
  addMonthsIso,
  buildMonthGrid,
  canCommitLiveParse,
  dateEntryValidationMessage,
  dateCalendarViewportPosition,
  formatIsoDateForInput,
  formatPartialAuDateInput,
  isoDateRangeError,
  parseIsoDateInput,
  startOfMonthIso,
  todayIsoUtc,
} from './date-entry-helpers';

// Re-export pure helpers so existing `from '.../date-entry-input'` imports keep working.
export {
  expandTwoDigitYear,
  parseIsoDateInput,
  isUnambiguousAuDateText,
  dateEntryValidationMessage,
  formatIsoDateForInput,
  formatPartialAuDateInput,
  isoDateRangeError,
  canCommitLiveParse,
  addDaysIso,
  addMonthsIso,
  startOfMonthIso,
  todayIsoUtc,
  buildMonthGrid,
  dateCalendarViewportPosition,
} from './date-entry-helpers';

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export interface DateEntryInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'size'
> {
  value?: string;
  density?: FieldDensity;
  size?: FieldControlSize;
  defaultValue?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  onValidationMessageChange?: (message: string) => void;
  /** Optional name for the visible AU-formatted input, alongside the ISO hidden input. */
  displayName?: string;
}

export const DateEntryInput = React.forwardRef<HTMLInputElement, DateEntryInputProps>(
  (
    {
      name,
      value,
      defaultValue,
      onChange,
      min,
      max,
      onValidationMessageChange,
      displayName,
      density,
      size,
      className,
      placeholder = 'DD/MM/YYYY',
      onBlur,
      disabled,
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const messageId = React.useId();
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const gridRef = React.useRef<HTMLDivElement | null>(null);
    const [submittedValue, setSubmittedValue] = React.useState(value ?? defaultValue ?? '');
    const [text, setText] = React.useState(() => formatIsoDateForInput(value ?? defaultValue));
    const [touched, setTouched] = React.useState(false);
    const [calendarOpen, setCalendarOpen] = React.useState(false);
    const [calendarPosition, setCalendarPosition] = React.useState<{
      top: number;
      left: number;
    } | null>(null);
    const lastExternal = React.useRef(value ?? defaultValue ?? '');

    const selectedIso =
      parseIsoDateInput(text) ||
      (submittedValue && ISO_DATE_RE.test(submittedValue) ? submittedValue : '') ||
      '';
    const initialView = selectedIso || (min && ISO_DATE_RE.test(min) ? min : '') || todayIsoUtc();
    const [viewMonth, setViewMonth] = React.useState(() => startOfMonthIso(initialView));
    const [focusDay, setFocusDay] = React.useState(() => selectedIso || initialView);

    React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement, []);

    React.useEffect(() => {
      if (value === undefined || value === lastExternal.current) return;
      lastExternal.current = value;
      setSubmittedValue(value);
      setText(formatIsoDateForInput(value));
    }, [value]);

    const validationMessage = dateEntryValidationMessage({ text, touched, min, max });
    const externalInvalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true';
    const showInvalid = Boolean(validationMessage) || externalInvalid;

    React.useEffect(() => {
      inputRef.current?.setCustomValidity(validationMessage);
      onValidationMessageChange?.(validationMessage);
    }, [onValidationMessageChange, validationMessage]);

    function commitIso(nextIso: string) {
      lastExternal.current = nextIso;
      setSubmittedValue(nextIso);
      onChange?.(nextIso);
    }

    function clearCommit() {
      lastExternal.current = '';
      setSubmittedValue('');
      onChange?.('');
    }

    function update(nextText: string) {
      const formattedText = formatPartialAuDateInput(nextText);
      setText(formattedText);

      const nextIso = parseIsoDateInput(formattedText);
      const nextRangeMessage = nextIso ? isoDateRangeError(nextIso, min, max) : null;

      // Live commit only for unambiguous complete forms (4-digit year / 8-digit compact).
      // Two-digit-year shorthand commits on blur so mid-type `03/08/20` is not 2020.
      if (
        canCommitLiveParse(formattedText) &&
        nextIso !== null &&
        nextIso !== '' &&
        nextRangeMessage === null
      ) {
        commitIso(nextIso);
        return;
      }

      // Incomplete or invalid: keep text, clear the ISO payload (existing contract).
      clearCommit();
    }

    function normaliseOnBlur(raw: string) {
      const formatted = formatPartialAuDateInput(raw);
      const nextIso = parseIsoDateInput(formatted);
      if (nextIso !== null && nextIso !== '' && isoDateRangeError(nextIso, min, max) === null) {
        setText(formatIsoDateForInput(nextIso));
        commitIso(nextIso);
        return;
      }
      setText(formatted);
      if (nextIso === null || nextIso === '' || isoDateRangeError(nextIso, min, max) !== null) {
        clearCommit();
      }
    }

    function openCalendar() {
      if (disabled) return;
      const base =
        (selectedIso && ISO_DATE_RE.test(selectedIso) && selectedIso) ||
        (min && ISO_DATE_RE.test(min) ? min : '') ||
        todayIsoUtc();
      setViewMonth(startOfMonthIso(base));
      setFocusDay(base);
      setCalendarOpen(true);
    }

    function closeCalendar(returnFocus: boolean) {
      setCalendarOpen(false);
      if (returnFocus) {
        // Defer so the popover unmount does not steal focus back.
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    }

    function selectDay(iso: string) {
      if (isoDateRangeError(iso, min, max)) return;
      setText(formatIsoDateForInput(iso));
      commitIso(iso);
      setTouched(true);
      closeCalendar(true);
    }

    // Focus the focused day cell when the calendar opens / focus day changes.
    React.useEffect(() => {
      if (!calendarOpen) return;
      const cell = gridRef.current?.querySelector<HTMLElement>(`[data-iso="${focusDay}"]`);
      cell?.focus();
    }, [calendarOpen, focusDay, viewMonth]);

    // Outside click + Escape
    React.useEffect(() => {
      if (!calendarOpen) return;

      function onKey(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeCalendar(true);
        }
      }

      function onPointer(event: MouseEvent) {
        const target = event.target as Node;
        if (gridRef.current?.contains(target)) return;
        if (triggerRef.current?.contains(target)) return;
        closeCalendar(false);
      }

      document.addEventListener('keydown', onKey, true);
      document.addEventListener('mousedown', onPointer);
      return () => {
        document.removeEventListener('keydown', onKey, true);
        document.removeEventListener('mousedown', onPointer);
      };
    }, [calendarOpen]);

    const positionCalendar = React.useCallback(() => {
      if (!triggerRef.current) return;
      const trigger = triggerRef.current.getBoundingClientRect();
      const panelWidth = gridRef.current?.offsetWidth ?? 280;
      const panelHeight = gridRef.current?.offsetHeight ?? 336;
      setCalendarPosition(
        dateCalendarViewportPosition({
          trigger,
          panelWidth,
          panelHeight,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        }),
      );
    }, []);

    React.useEffect(() => {
      if (!calendarOpen) {
        setCalendarPosition(null);
        return;
      }
      positionCalendar();
      const reposition = () => positionCalendar();
      window.addEventListener('resize', reposition);
      window.addEventListener('scroll', reposition, true);
      return () => {
        window.removeEventListener('resize', reposition);
        window.removeEventListener('scroll', reposition, true);
      };
    }, [calendarOpen, positionCalendar, viewMonth]);

    function onGridKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
      const key = event.key;
      if (key === 'Tab') {
        const focusable = gridRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
        return;
      }
      // Month navigation buttons retain native Enter/Space activation.
      if (!(event.target instanceof HTMLElement) || !event.target.matches('button[data-iso]'))
        return;
      let delta = 0;
      if (key === 'ArrowLeft') delta = -1;
      else if (key === 'ArrowRight') delta = 1;
      else if (key === 'ArrowUp') delta = -7;
      else if (key === 'ArrowDown') delta = 7;
      else if (key === 'Home') {
        event.preventDefault();
        const start = startOfMonthIso(focusDay);
        setFocusDay(start);
        setViewMonth(start);
        return;
      } else if (key === 'End') {
        event.preventDefault();
        const match = ISO_DATE_RE.exec(focusDay);
        if (!match) return;
        const lastDay = new Date(Date.UTC(Number(match[1]), Number(match[2]), 0)).getUTCDate();
        const end = `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`;
        setFocusDay(end);
        return;
      } else if (key === 'PageUp') {
        event.preventDefault();
        const next = addMonthsIso(focusDay, event.shiftKey ? -12 : -1);
        setFocusDay(next);
        setViewMonth(startOfMonthIso(next));
        return;
      } else if (key === 'PageDown') {
        event.preventDefault();
        const next = addMonthsIso(focusDay, event.shiftKey ? 12 : 1);
        setFocusDay(next);
        setViewMonth(startOfMonthIso(next));
        return;
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (!isoDateRangeError(focusDay, min, max)) selectDay(focusDay);
        return;
      } else {
        return;
      }

      event.preventDefault();
      const next = addDaysIso(focusDay, delta);
      setFocusDay(next);
      setViewMonth(startOfMonthIso(next));
    }

    const viewMatch = ISO_DATE_RE.exec(viewMonth);
    const viewYear = viewMatch ? Number(viewMatch[1]) : new Date().getUTCFullYear();
    const viewMonthNum = viewMatch ? Number(viewMatch[2]) : new Date().getUTCMonth() + 1;
    const cells = buildMonthGrid(viewYear, viewMonthNum);

    return (
      <div className="relative w-full" data-date-entry-input>
        {name ? (
          <input type="hidden" name={name} value={submittedValue} disabled={disabled} />
        ) : null}
        <div className="relative w-full">
          <input
            {...props}
            ref={inputRef}
            name={displayName}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder}
            value={text}
            maxLength={10}
            disabled={disabled}
            aria-invalid={showInvalid ? true : undefined}
            aria-describedby={
              [props['aria-describedby'], validationMessage ? messageId : undefined]
                .filter(Boolean)
                .join(' ') || undefined
            }
            className={cn(
              fieldControlClassName,
              fieldDensityClassName(density, size),
              className,
              'pr-10',
              // AFTER `className` deliberately. tailwind-merge is last-wins and
              // call sites pass their own border utility, so putting this first
              // meant a rejected date rendered identically to an accepted one.
              // The time field had exactly this bug; the date field kept it.
              showInvalid &&
                'border-status-danger-fg focus:border-status-danger-fg focus:ring-focus',
            )}
            title={validationMessage ?? undefined}
            onChange={(event) => update(event.target.value)}
            onBlur={(event) => {
              setTouched(true);
              normaliseOnBlur(event.target.value);
              onBlur?.(event);
            }}
          />
          <button
            ref={triggerRef}
            type="button"
            disabled={disabled}
            aria-label="Open calendar"
            aria-haspopup="dialog"
            aria-expanded={calendarOpen}
            className={cn(
              'absolute right-0.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-control',
              'text-text-muted hover:bg-surface-soft hover:text-text-heading',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            onClick={() => {
              if (calendarOpen) closeCalendar(true);
              else openCalendar();
            }}
          >
            <CalendarIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        {validationMessage && (
          <p id={messageId} className="mt-1 text-caption text-status-danger-fg">
            {validationMessage}
          </p>
        )}
        {calendarOpen && typeof document !== 'undefined'
          ? createPortal(
              <div
                ref={gridRef}
                role="dialog"
                aria-label="Choose date"
                data-date-entry-calendar
                style={{
                  position: 'fixed',
                  zIndex: 220,
                  top: calendarPosition?.top ?? -9999,
                  left: calendarPosition?.left ?? -9999,
                  visibility: calendarPosition ? 'visible' : 'hidden',
                }}
                className={cn(
                  // Keep the existing modal stacking order in the positioning style.
                  'w-72 max-w-full rounded-overlay border border-border bg-surface-sheet p-3 shadow-popover',
                )}
                onKeyDown={onGridKeyDown}
              >
                <div className="mb-2 flex items-center justify-between gap-1">
                  <button
                    type="button"
                    aria-label="Previous month"
                    className="flex h-8 w-8 items-center justify-center rounded-control text-text-muted hover:bg-surface-soft"
                    onClick={() => {
                      const next = addMonthsIso(viewMonth, -1);
                      setViewMonth(startOfMonthIso(next));
                      setFocusDay((prev) => addMonthsIso(prev, -1));
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <div className="text-sm font-semibold text-text-heading" aria-live="polite">
                    {MONTH_LABELS[viewMonthNum - 1]} {viewYear}
                  </div>
                  <button
                    type="button"
                    aria-label="Next month"
                    className="flex h-8 w-8 items-center justify-center rounded-control text-text-muted hover:bg-surface-soft"
                    onClick={() => {
                      const next = addMonthsIso(viewMonth, 1);
                      setViewMonth(startOfMonthIso(next));
                      setFocusDay((prev) => addMonthsIso(prev, 1));
                    }}
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                <div role="grid" aria-label={`${MONTH_LABELS[viewMonthNum - 1]} ${viewYear}`}>
                  <div role="row" className="mb-1 grid grid-cols-7 gap-0.5">
                    {WEEKDAY_LABELS.map((label) => (
                      <div
                        key={label}
                        role="columnheader"
                        className="py-1 text-center text-caption font-semibold uppercase tracking-wide text-text-muted"
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                  {Array.from({ length: cells.length / 7 }, (_, rowIndex) => (
                    <div key={rowIndex} role="row" className="grid grid-cols-7 gap-0.5">
                      {cells.slice(rowIndex * 7, rowIndex * 7 + 7).map((iso, colIndex) => {
                        if (!iso) {
                          return (
                            <div
                              key={`pad-${rowIndex}-${colIndex}`}
                              role="gridcell"
                              aria-hidden="true"
                              className="h-8"
                            />
                          );
                        }
                        const dayNum = Number(iso.slice(8, 10));
                        const outOfRange = Boolean(isoDateRangeError(iso, min, max));
                        const isSelected = selectedIso === iso;
                        const isFocused = focusDay === iso;
                        return (
                          <button
                            key={iso}
                            type="button"
                            role="gridcell"
                            data-iso={iso}
                            tabIndex={isFocused ? 0 : -1}
                            aria-selected={isSelected}
                            aria-disabled={outOfRange || undefined}
                            disabled={outOfRange}
                            aria-label={formatIsoDateForInput(iso)}
                            className={cn(
                              'h-8 rounded-control text-xs font-medium',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                              outOfRange && 'cursor-not-allowed text-text-muted',
                              !outOfRange &&
                                !isSelected &&
                                'text-text-heading hover:bg-surface-soft',
                              isSelected && 'bg-action text-action-foreground hover:bg-action',
                              !isSelected && isFocused && 'ring-2 ring-focus',
                            )}
                            onClick={() => selectDay(iso)}
                            onFocus={() => setFocusDay(iso)}
                          >
                            {dayNum}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>,
              document.body,
            )
          : null}
      </div>
    );
  },
);
DateEntryInput.displayName = 'DateEntryInput';
